/**
 * Subscriptions list — главный экран управления UUID/ключами.
 *
 * Колонки: ID, юзер, тариф, ярлык, sub-URL (copy), статус, до-дней,
 *          трафик-всего, кол-во reset, действия.
 * Фильтры: tariff, status (active/expired/revoked).
 * Действия: 👁 Просмотр, ✋ Отозвать, ▶ Включить, 🔄 Reset key, ➕ Продлить.
 */
import { List, ShowButton, CreateButton, useTable } from "@refinedev/antd";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import {
  Table, Tag, Space, Button, Tooltip, Select, Modal,
  InputNumber, Typography, Divider, App as AntApp,
  Input,
} from "antd";
import {
  StopOutlined, CheckCircleOutlined, ReloadOutlined,
  PlusCircleOutlined, CopyOutlined, StarFilled, StarOutlined,
  LinkOutlined, ExportOutlined, ClockCircleOutlined, WarningOutlined,
  DisconnectOutlined, MessageOutlined,
  SearchOutlined, ClearOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { useState, useEffect } from "react";
import { API_URL } from "../../dataProvider";
import { SubStatusTag } from "../../components/SubStatusTag";
import {
  TARIFF_LABEL, TARIFF_COLOR,
  type SubscriptionListItem, type Tariff,
} from "../../types";
import { fmtBytes, fmtUserName, daysLeft } from "../../utils";

export const SubscriptionList = () => {
  // App.useApp() hook — обязательно для antd v5 + React 19.
  //   • modal   — static Modal.confirm() silently fails без контекста <App>
  //     wrapper'а (никакой ошибки, окно просто не открывается). Это и был
  //     основной баг "нажимаю отвязать TG, ничего не происходит".
  //   • message — static message.xxx() в StrictMode эмитит warning и теряет
  //     theme-контекст; хук возвращает инстанс с правильным контекстом.
  // См. https://ant.design/components/app — раздел "React 19 compatibility".
  const { modal, message } = AntApp.useApp();

  const { tableProps, searchFormProps, sorters, filters, setFilters } =
    useTable<SubscriptionListItem>({
      syncWithLocation: true,
      pagination: { mode: "server", pageSize: 25 },
      sorters: { initial: [{ field: "id", order: "desc" }] },
    });

  // Поиск: q-input debounced на 300мс. Локальное состояние input'а
  // отделено от URL/filters — печатаем плавно, фетч не дёргаем на каждый
  // keystroke. См. useEffect ниже.
  const qFilter = filters.find(f => "field" in f && f.field === "q");
  const qFromUrl = (qFilter && "value" in qFilter ? String(qFilter.value) : "") || "";
  const [qInput, setQInput] = useState<string>(qFromUrl);
  useEffect(() => {
    // Синхронизация: если фильтр в URL изменился (back-button, paste-ссылка),
    // обновить input. Без этого юзер видит «пустой инпут, но фильтр работает».
    setQInput(qFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qFromUrl]);

  useEffect(() => {
    // Debounce 300мс: пользователь печатает «sergey» — мы дёрнем фильтр
    // только когда он сделает паузу. Иначе на каждом keystroke был бы
    // refetch (~6 запросов на слово).
    const timer = setTimeout(() => {
      const cleaned = qInput.trim();
      if (cleaned === qFromUrl) return;  // ничего не изменилось
      setFilters([
        ...filters.filter(f => "field" in f && f.field !== "q"),
        ...(cleaned ? [{ field: "q", operator: "eq" as const, value: cleaned }] : []),
      ], "replace");
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const { mutate: callAction, isLoading: actionLoading } = useCustomMutation();
  const invalidate = useInvalidate();

  const action = (
    id: number, what: "revoke" | "unrevoke" | "reset_key", successText: string,
  ) => {
    callAction({
      url: `${API_URL}/admin/subscriptions/${id}/${what}`,
      method: "post", values: {},
      successNotification: { message: successText, type: "success" },
      errorNotification: (e) => ({
        message: `Ошибка: ${(e as any)?.message || e}`, type: "error",
      }),
    }, {
      onSuccess: () => invalidate({ resource: "subscriptions", invalidates: ["list"] }),
    });
  };

  const [extending, setExtending] = useState<{ id: number; days: number } | null>(null);
  const [extendBusy, setExtendBusy] = useState(false);
  // Direct fetch (минуя useCustomMutation) — Refine v5 в нашей сборке
  // пробрасывает body неконсистентно (приходил 422 в backend, body пустой).
  // Прямой fetch с body=JSON.stringify({days}) работает гарантированно.
  const submitExtend = async () => {
    if (!extending) return;
    setExtendBusy(true);
    try {
      const r = await fetch(
        `${API_URL}/admin/subscriptions/${extending.id}/extend`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ days: extending.days }),
        }
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${txt.slice(0, 200)}`);
        return;
      }
      message.success(
        `Подписка #${extending.id} продлена на ${extending.days} дней`
      );
      invalidate({ resource: "subscriptions", invalidates: ["list"] });
      setExtending(null);
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setExtendBusy(false);
    }
  };

  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success("Sub-URL скопирован");
  };

  // ─── BULK SELECTION & ACTIONS ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkExtendOpen, setBulkExtendOpen] = useState(false);
  const [bulkExtendDays, setBulkExtendDays] = useState(30);
  const [bulkBindOpen, setBulkBindOpen] = useState(false);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Хранилища результатов bulk-операций (для модалок).
  const [bindResults, setBindResults] = useState<Array<{ id: number; label: string; url: string }>>([]);
  const [exportResults, setExportResults] = useState<Array<{ id: number; label: string; sub_url: string; bind_url: string | null }>>([]);

  // Все строки из текущей страницы — нужны для отображения ярлыка и sub_url
  // выбранных подписок (без N+1 запросов к API).
  const rowsOnPage: SubscriptionListItem[] = (tableProps.dataSource as any) ?? [];
  const labelFor = (r: SubscriptionListItem) =>
    r.label || fmtUserName(r) || `#${r.id}`;

  /**
   * Параллельный bulk-вызов одного POST endpoint на N подписок.
   * Возвращает Map<id, success> + показывает summary toast.
   */
  const bulkPost = async (
    path: (id: number) => string,
    body: any,
    okLabel: string,
  ): Promise<Map<number, boolean>> => {
    const result = new Map<number, boolean>();
    setBulkBusy(true);
    try {
      await Promise.all(selectedIds.map(async (id) => {
        try {
          const r = await fetch(`${API_URL}${path(id)}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: body !== undefined ? JSON.stringify(body) : undefined,
          });
          result.set(id, r.ok);
        } catch {
          result.set(id, false);
        }
      }));
      const okCount = Array.from(result.values()).filter(Boolean).length;
      const failCount = selectedIds.length - okCount;
      if (failCount === 0) {
        message.success(`${okLabel}: ${okCount}/${selectedIds.length}`);
      } else {
        message.warning(`${okLabel}: ${okCount} ok, ${failCount} ошибок`);
      }
      invalidate({ resource: "subscriptions", invalidates: ["list"] });
    } finally {
      setBulkBusy(false);
    }
    return result;
  };

  // ── 1. Bulk Extend (продлить N дней)
  const doBulkExtend = async () => {
    if (selectedIds.length === 0 || bulkExtendDays < 1) return;
    await bulkPost(
      (id) => `/admin/subscriptions/${id}/extend`,
      { days: bulkExtendDays },
      `Продлено на ${bulkExtendDays}д`,
    );
    setBulkExtendOpen(false);
  };

  // ── 2. Bulk Revoke (выключить)
  const doBulkRevoke = () => {
    modal.confirm({
      title: `Выключить ${selectedIds.length} подписок?`,
      content: "Ключи отзываются (hysteria_revoked_at=now). Можно вернуть через /unrevoke.",
      okText: "Выключить", okButtonProps: { danger: true },
      onOk: async () => { await bulkPost((id) => `/admin/subscriptions/${id}/revoke`, undefined, "Отозвано"); },
    });
  };

  // ── 2b. Bulk Unbind TG — освободить подписку от текущего владельца
  //         (создаёт fake-юзера, переписывает user_id). Use-case:
  //         «ссылку привязки кликнул не тот человек».
  const doBulkUnbind = () => {
    modal.confirm({
      title: `🔓 Отвязать TG у ${selectedIds.length} подписок?`,
      content: (
        <div>
          <p>Текущий TG-владелец будет отключён от подписки —
          подписка станет «бесхозной» (привязана к fake-юзеру).</p>
          <p>После этого можно сгенерировать новую <b>ссылку для привязки</b>
          и отдать её правильному человеку.</p>
          <p><b>Защита:</b> если среди выбранных есть подписка, привязанная к
          админу — она пропускается (Refine-доступ не ломаем).</p>
        </div>
      ),
      okText: "Отвязать", okButtonProps: { danger: true },
      onOk: async () => { await bulkPost((id) => `/admin/subscriptions/${id}/unbind-tg`, undefined, "Отвязано"); },
    });
  };

  // Per-row single unbind
  const singleUnbind = (id: number) => {
    modal.confirm({
      title: `🔓 Отвязать TG у подписки #${id}?`,
      content: "Текущий TG-владелец будет отключён. Потом сгенерируйте новую ссылку привязки.",
      okText: "Отвязать", okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await fetch(`${API_URL}/admin/subscriptions/${id}/unbind-tg`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (r.ok) {
            const data = await r.json();
            message.success(`#${id} отвязана от tg_id=${data.old_user_id}`);
            invalidate({ resource: "subscriptions", invalidates: ["list"] });
          } else {
            const txt = await r.text().catch(() => r.statusText);
            message.error(`Ошибка ${r.status}: ${txt.slice(0, 200)}`);
          }
        } catch (e) {
          message.error(`Сетевая ошибка: ${(e as Error).message}`);
        }
      },
    });
  };

  // ── 3. Bulk Ban (revoke + flagged_abuse + bot_blocked)
  const doBulkBan = () => {
    modal.confirm({
      title: `🚫 Забанить ${selectedIds.length} подписок?`,
      content: "Жёстче чем выключить: ключи отозваны + юзеры помечены как abuser + бот их заблокирует.",
      okText: "Забанить", okButtonProps: { danger: true },
      onOk: async () => { await bulkPost((id) => `/admin/subscriptions/${id}/ban`, undefined, "Забанено"); },
    });
  };

  // ── 4. Bulk Reset Key (новый sub_token + password)
  const doBulkResetKey = () => {
    modal.confirm({
      title: `Сбросить ключи у ${selectedIds.length} подписок?`,
      content: "Старые sub_token и hysteria_password становятся невалидными. Юзерам придётся импортировать НОВЫЙ ключ.",
      okText: "Сбросить", okButtonProps: { danger: true },
      onOk: async () => { await bulkPost((id) => `/admin/subscriptions/${id}/reset_key`, undefined, "Ключи сброшены"); },
    });
  };

  // ── 5. Bulk Favorite (PATCH is_favorite=true)
  const doBulkFavorite = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      await Promise.all(selectedIds.map(async (id) => {
        try {
          await fetch(`${API_URL}/admin/subscriptions/${id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ is_favorite: true }),
          });
        } catch {}
      }));
      message.success(`⭐ В избранное: ${selectedIds.length}`);
      invalidate({ resource: "subscriptions", invalidates: ["list"] });
    } finally {
      setBulkBusy(false);
    }
  };

  // ── 6. Bulk Binding-link — собирает URL для каждой подписки и показывает в модалке.
  const doBulkBind = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    const out: Array<{ id: number; label: string; url: string }> = [];
    try {
      await Promise.all(selectedIds.map(async (id) => {
        const row = rowsOnPage.find(r => r.id === id);
        const lbl = row ? labelFor(row) : `#${id}`;
        try {
          const r = await fetch(`${API_URL}/admin/subscriptions/${id}/binding-link`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (r.ok) {
            const data = await r.json();
            out.push({ id, label: lbl, url: data.url || "" });
          } else {
            out.push({ id, label: lbl, url: "(ошибка получения ссылки)" });
          }
        } catch {
          out.push({ id, label: lbl, url: "(сетевая ошибка)" });
        }
      }));
      // Сортируем в том же порядке что и selectedIds для предсказуемости.
      out.sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id));
      setBindResults(out);
      setBulkBindOpen(true);
    } finally {
      setBulkBusy(false);
    }
  };

  // ── 7. Bulk Export — ярлык + привязка + ключ для каждой подписки.
  const doBulkExport = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    const out: Array<{ id: number; label: string; sub_url: string; bind_url: string | null }> = [];
    try {
      await Promise.all(selectedIds.map(async (id) => {
        const row = rowsOnPage.find(r => r.id === id);
        const lbl = row ? labelFor(row) : `#${id}`;
        const subUrl = row?.sub_url || "";
        let bindUrl: string | null = null;
        try {
          const r = await fetch(`${API_URL}/admin/subscriptions/${id}/binding-link`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (r.ok) bindUrl = (await r.json()).url || null;
        } catch {}
        out.push({ id, label: lbl, sub_url: subUrl, bind_url: bindUrl });
      }));
      out.sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id));
      setExportResults(out);
      setBulkExportOpen(true);
    } finally {
      setBulkBusy(false);
    }
  };

  // Per-row single bind — открывает тот же модал, но с одной строкой.
  const singleBind = async (id: number) => {
    setSelectedIds([id]);
    setTimeout(doBulkBind, 0);  // даём React время обновить selectedIds
  };

  const copyAllBinds = () => {
    const text = bindResults
      .map(b => `${b.label}\n${b.url}`)
      .join("\n──────────\n");
    navigator.clipboard.writeText(text);
    message.success(`Скопировано ${bindResults.length} привязок`);
  };

  const copyAllExport = () => {
    const text = exportResults
      .map(e => `${e.label}\n${e.bind_url || "(нет привязки)"}\n${e.sub_url}`)
      .join("\n──────────\n");
    navigator.clipboard.writeText(text);
    message.success(`Скопировано ${exportResults.length} комплектов`);
  };

  const tariffFilter = filters.find(f => "field" in f && f.field === "tariff");
  const statusFilter = filters.find(f => "field" in f && f.field === "status");
  const onlineFilter = filters.find(f => "field" in f && f.field === "online");
  const favFilter    = filters.find(f => "field" in f && f.field === "is_favorite");
  const favActive    = favFilter && "value" in favFilter && String(favFilter.value) === "true";
  const deviceLimitFilter = filters.find(f => "field" in f && f.field === "device_limit");
  const dlValue = (deviceLimitFilter && "value" in deviceLimitFilter
                   ? String(deviceLimitFilter.value) : undefined);

  // Признак «есть активные фильтры» — для кнопки «Очистить»
  const hasActiveFilters = Boolean(
    qInput || tariffFilter || statusFilter || onlineFilter || favActive || dlValue
  );

  const clearAllFilters = () => {
    setQInput("");
    setFilters([], "replace");
  };

  return (
    <List
      title="UUID-ключи"
      breadcrumb={false}
      headerButtons={() => (
        <CreateButton type="primary" icon={<PlusCircleOutlined />}>
          Создать UUID
        </CreateButton>
      )}
    >
      <Space style={{ marginBottom: 12 }} wrap>
        {/* 🔍 Универсальный поиск — debounced 300мс. Ищет по ярлыку, имени
            юзера, @username, admin_notes, tg_id, sub_token. */}
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          placeholder="Поиск: ярлык, имя, @username, tg_id, токен…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          style={{ width: 320 }}
        />
        <Select
          style={{ width: 180 }} placeholder="Все тарифы" allowClear
          value={(tariffFilter && "value" in tariffFilter ? tariffFilter.value : undefined) as Tariff | undefined}
          onChange={(v) => setFilters([
            { field: "tariff", operator: "eq", value: v ?? "" },
            ...filters.filter(f => "field" in f && f.field !== "tariff"),
          ], "replace")}
          options={[
            { value: "basic", label: TARIFF_LABEL.basic },
            { value: "parental", label: TARIFF_LABEL.parental },
            { value: "premium", label: TARIFF_LABEL.premium },
          ]}
        />
        <Select
          style={{ width: 180 }} placeholder="Все статусы" allowClear
          value={(statusFilter && "value" in statusFilter ? statusFilter.value : undefined) as string | undefined}
          onChange={(v) => setFilters([
            { field: "status", operator: "eq", value: v ?? "" },
            ...filters.filter(f => "field" in f && f.field !== "status"),
          ], "replace")}
          options={[
            { value: "active", label: "✅ Активные" },
            { value: "expired", label: "⌛ Истёкшие" },
            { value: "revoked", label: "🔒 Отозванные" },
          ]}
        />
        <Select
          style={{ width: 170 }} placeholder="Лимит устройств" allowClear
          value={dlValue}
          onChange={(v) => setFilters([
            ...filters.filter(f => "field" in f && f.field !== "device_limit"),
            ...(v ? [{ field: "device_limit", operator: "eq" as const, value: v }] : []),
          ], "replace")}
          options={[
            { value: "1",  label: "1 устройство" },
            { value: "2",  label: "2 устройства" },
            { value: "3",  label: "3 устройства" },
            { value: "5",  label: "5 устройств" },
            { value: "10", label: "10 устройств" },
          ]}
        />
        <Select
          style={{ width: 200 }} placeholder="Активность" allowClear
          value={(onlineFilter && "value" in onlineFilter
                  ? onlineFilter.value : undefined) as string | undefined}
          onChange={(v) => setFilters([
            { field: "online", operator: "eq", value: v ?? "" },
            ...filters.filter(f => "field" in f && f.field !== "online"),
          ], "replace")}
          options={[
            { value: "true", label: "🟢 Только онлайн сейчас" },
          ]}
        />
        <Button
          type={favActive ? "primary" : "default"}
          icon={favActive ? <StarFilled style={{ color: "#fff" }} /> : <StarOutlined />}
          onClick={() => setFilters([
            { field: "is_favorite", operator: "eq", value: favActive ? "" : "true" },
            ...filters.filter(f => "field" in f && f.field !== "is_favorite"),
          ], "replace")}
          title="Показать только избранные (VIP — без sharing-проверок)"
        >
          {favActive ? "⭐ Избранные" : "Избранные"}
        </Button>
        {hasActiveFilters && (
          <Tooltip title="Очистить все фильтры и поиск">
            <Button
              icon={<ClearOutlined />}
              onClick={clearAllFilters}
              danger
            >
              Сбросить фильтры
            </Button>
          </Tooltip>
        )}
      </Space>

      {/* Bulk-action bar — появляется когда что-то выбрано. Sticky сверху,
          чтобы юзер видел его при скролле длинной таблицы. */}
      {selectedIds.length > 0 && (
        <div style={{
          position: "sticky", top: 0, zIndex: 10,
          marginBottom: 12, padding: "10px 14px",
          background: "#e6f4ff", border: "1px solid #91caff",
          borderRadius: 6,
          display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
        }}>
          <Typography.Text strong style={{ marginRight: 8 }}>
            ✓ Выбрано: <span style={{ color: "#1677ff" }}>{selectedIds.length}</span>
          </Typography.Text>
          <Button size="small" icon={<ClockCircleOutlined />} loading={bulkBusy}
                  onClick={() => setBulkExtendOpen(true)}>
            Продлить N дней
          </Button>
          <Button size="small" icon={<LinkOutlined />} loading={bulkBusy}
                  onClick={doBulkBind}>
            Привязки TG
          </Button>
          <Button size="small" icon={<DisconnectOutlined />} loading={bulkBusy}
                  onClick={doBulkUnbind} danger>
            🔓 Отвязать TG
          </Button>
          <Button size="small" icon={<ExportOutlined />} loading={bulkBusy}
                  onClick={doBulkExport}>
            Экспорт ключей
          </Button>
          <Button size="small" icon={<StarFilled style={{ color: "#faad14" }} />}
                  loading={bulkBusy} onClick={doBulkFavorite}>
            В избранное
          </Button>
          <Button size="small" icon={<ReloadOutlined />} loading={bulkBusy}
                  onClick={doBulkResetKey}>
            Сбросить ключи
          </Button>
          <Button size="small" danger icon={<StopOutlined />} loading={bulkBusy}
                  onClick={doBulkRevoke}>
            Выключить
          </Button>
          <Button size="small" danger type="primary" icon={<WarningOutlined />}
                  loading={bulkBusy} onClick={doBulkBan}>
            🚫 Бан
          </Button>
          <Button size="small" type="text"
                  onClick={() => setSelectedIds([])}>
            ✕ Снять выбор
          </Button>
        </div>
      )}

      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys as number[]),
          preserveSelectedRowKeys: true,
          // Чекбокс в шапке выбирает текущую страницу. При пагинации
          // preserveSelectedRowKeys сохраняет ранее выбранные на других страницах.
        }}>
        <Table.Column dataIndex="id" title="ID" sorter width={70} />
        <Table.Column<SubscriptionListItem>
          title="Юзер"
          render={(_, r) => (
            <Link to={`/users/${r.user_id}`}>{fmtUserName(r)}</Link>
          )}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="tariff" title="Тариф" sorter
          render={(_, r) => (
            <Tag color={TARIFF_COLOR[r.tariff]}>{r.tariff_title || r.tariff}</Tag>
          )}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="label" title="Ярлык" sorter
          render={(_, r) => (
            <Space size={4} style={{ alignItems: "center" }}>
              {r.is_favorite && (
                <Tooltip title="⭐ В избранном — без sharing-проверок">
                  <StarFilled style={{ color: "#faad14", fontSize: 14 }} />
                </Tooltip>
              )}
              <span style={{ opacity: r.label ? 1 : 0.4 }}>
                {r.label || "—"}
              </span>
            </Space>
          )}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="device_limit" sorter
          title={<Tooltip title="Лимит одновременных устройств — поставленный админом">📱 Лимит</Tooltip>}
          width={90}
          render={(_, r) => (
            <Tag color="blue" style={{ fontSize: 12 }}>
              {r.device_limit}
            </Tag>
          )}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="devices_used" sorter
          title={<Tooltip title="Сколько разных устройств реально подключалось за 30 дней (уникальные IP)">🔌 Использовано</Tooltip>}
          width={120}
          render={(_, r) => {
            const used = r.devices_used ?? 0;
            const limit = r.device_limit ?? 0;
            // Цвет: зелёный в пределах, оранжевый = ровно, красный = превысил.
            const color = used === 0 ? "default"
                        : limit > 0 && used > limit ? "red"
                        : limit > 0 && used === limit ? "orange"
                        : "green";
            return (
              <Tooltip title={`${used} уникальных IP за 30 дней`}>
                <Tag color={color as any} style={{ fontSize: 12 }}>
                  {used > 0 ? `${used}` : "—"}
                </Tag>
              </Tooltip>
            );
          }}
        />
        <Table.Column<SubscriptionListItem>
          title="Sub-URL"
          render={(_, r) => (
            <Tooltip title="Скопировать">
              <Button size="small" type="text" icon={<CopyOutlined />}
                      onClick={() => copy(r.sub_url)}>
                {r.sub_token.slice(0, 8)}…
              </Button>
            </Tooltip>
          )}
        />
        <Table.Column<SubscriptionListItem>
          title="Статус" render={(_, r) => <SubStatusTag s={r} />} />
        <Table.Column<SubscriptionListItem>
          dataIndex="end_at" title="До" sorter
          render={(v: string | null) => {
            const d = daysLeft(v);
            if (d === null) return "—";
            const color = d <= 0 ? "red" : d <= 3 ? "orange" : d <= 7 ? "gold" : "default";
            return <Tag color={color as any}>{d > 0 ? `${d}д` : "истекла"}</Tag>;
          }}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="bytes_total" title="Трафик" sorter
          render={(_, r) => <Typography.Text strong>{fmtBytes(r.bytes_total)}</Typography.Text>}
        />
        <Table.Column<SubscriptionListItem>
          dataIndex="online_now" title="🟢 Online" sorter width={110}
          render={(_, r) => r.online_now > 0
            ? <Tag color="green" style={{ fontSize: 12, padding: "1px 8px" }}>
                {r.online_now}
              </Tag>
            : <span style={{ opacity: 0.4 }}>—</span>}
        />
        <Table.Column<SubscriptionListItem>
          title="Reset"
          render={(_, r) => r.uuid_reset_count > 0
            ? <Tag>{r.uuid_reset_count}</Tag>
            : <span style={{ opacity: 0.4 }}>—</span>}
        />
        <Table.Column<SubscriptionListItem>
          title="Действия" fixed="right" width={360}
          render={(_, r) => (
            <Space size={4}>
              <ShowButton hideText size="small" recordItemId={r.id} />
              {r.user_id > 0 && (
                <Tooltip title="💬 Написать в Telegram">
                  <Link to={`/dialogs?user=${r.user_id}`}>
                    <Button size="small" type="text"
                            icon={<MessageOutlined />}
                            style={{ color: "#1677ff" }} />
                  </Link>
                </Tooltip>
              )}
              {r.hysteria_revoked_at ? (
                <Tooltip title="Включить (снять revoke)">
                  <Button size="small" type="text" icon={<CheckCircleOutlined />}
                          loading={actionLoading}
                          onClick={() => action(r.id, "unrevoke", `#${r.id} включена`)} />
                </Tooltip>
              ) : (
                <Tooltip title="Отозвать (выключить)">
                  <Button size="small" type="text" danger icon={<StopOutlined />}
                          loading={actionLoading}
                          onClick={() => action(r.id, "revoke", `#${r.id} отозвана`)} />
                </Tooltip>
              )}
              <Tooltip title="Сбросить ключ (новый sub_token + password)">
                <Button size="small" type="text" icon={<ReloadOutlined />}
                        loading={actionLoading}
                        onClick={() => action(r.id, "reset_key", `#${r.id} ключ сброшен`)} />
              </Tooltip>
              <Tooltip title="Продлить">
                <Button size="small" type="text" icon={<PlusCircleOutlined />}
                        onClick={() => setExtending({ id: r.id, days: 30 })} />
              </Tooltip>
              <Tooltip title="🔗 Получить ссылку для привязки TG">
                <Button size="small" type="text" icon={<LinkOutlined />}
                        loading={bulkBusy}
                        onClick={() => singleBind(r.id)} />
              </Tooltip>
              <Tooltip title="🔓 Отвязать TG (если по ссылке кликнул не тот человек)">
                <Button size="small" type="text" danger icon={<DisconnectOutlined />}
                        onClick={() => singleUnbind(r.id)} />
              </Tooltip>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={`Продлить подписку #${extending?.id}`}
        open={extending !== null}
        onCancel={() => setExtending(null)}
        onOk={submitExtend}
        confirmLoading={extendBusy}
        okText="Продлить"
      >
        <p>На сколько дней продлить?</p>
        <InputNumber min={1} max={365} value={extending?.days ?? 30}
          style={{ width: "100%" }}
          onChange={(v) => setExtending(extending
            ? { ...extending, days: Number(v) || 30 } : null)} />
      </Modal>

      {/* ===== Bulk Extend ===== */}
      <Modal
        title={`⏰ Массовое продление (${selectedIds.length} подписок)`}
        open={bulkExtendOpen}
        onCancel={() => setBulkExtendOpen(false)}
        onOk={doBulkExtend}
        confirmLoading={bulkBusy}
        okText={`Продлить на ${bulkExtendDays} д`}
      >
        <Typography.Paragraph>
          На сколько дней продлить срок всем выбранным подпискам?
        </Typography.Paragraph>
        <Space>
          {[7, 14, 30, 60, 90, 180, 365].map(d => (
            <Button key={d} size="small"
                    type={bulkExtendDays === d ? "primary" : "default"}
                    onClick={() => setBulkExtendDays(d)}>
              {d} д
            </Button>
          ))}
        </Space>
        <InputNumber min={1} max={365} value={bulkExtendDays}
          style={{ width: "100%", marginTop: 12 }}
          onChange={(v) => setBulkExtendDays(Number(v) || 30)} />
      </Modal>

      {/* ===== Bulk Binding-link list ===== */}
      <Modal
        title={`🔗 Привязка TG (${bindResults.length} ссылок)`}
        open={bulkBindOpen}
        onCancel={() => setBulkBindOpen(false)}
        width={720}
        footer={[
          <Button key="cancel" onClick={() => setBulkBindOpen(false)}>Закрыть</Button>,
          <Button key="copy" type="primary" icon={<CopyOutlined />}
                  onClick={copyAllBinds}>
            📋 Скопировать всё
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Ссылки действуют 24 часа. Отправь каждую конкретному юзеру — когда
          он кликнет, его TG привяжется к подписке.
        </Typography.Paragraph>
        <div style={{ maxHeight: 480, overflowY: "auto" }}>
          {bindResults.map((b, i) => (
            <div key={b.id}>
              <div style={{ padding: "10px 0" }}>
                <Typography.Text strong>{b.label}</Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text code copyable style={{ fontSize: 12, wordBreak: "break-all" }}>
                    {b.url}
                  </Typography.Text>
                </div>
              </div>
              {i < bindResults.length - 1 && <Divider style={{ margin: 0 }} />}
            </div>
          ))}
        </div>
      </Modal>

      {/* ===== Bulk Export keys (ярлык + привязка + ключ + черта) ===== */}
      <Modal
        title={`📋 Экспорт ключей (${exportResults.length} подписок)`}
        open={bulkExportOpen}
        onCancel={() => setBulkExportOpen(false)}
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setBulkExportOpen(false)}>Закрыть</Button>,
          <Button key="copy" type="primary" icon={<CopyOutlined />}
                  onClick={copyAllExport}>
            📋 Скопировать всё
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Для каждой подписки: <b>ярлык</b> → <b>ссылка привязки TG</b> (24ч) →
          <b> ключ</b> (sub-URL для VPN-приложения). Между записями — разделитель.
        </Typography.Paragraph>
        <div style={{ maxHeight: 520, overflowY: "auto" }}>
          {exportResults.map((e, i) => (
            <div key={e.id}>
              <div style={{ padding: "10px 0" }}>
                <Typography.Text strong>{e.label}</Typography.Text>
                <div style={{ marginTop: 6 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>🔗 Привязка TG:</Typography.Text>
                  <div>
                    {e.bind_url ? (
                      <Typography.Text code copyable style={{ fontSize: 12, wordBreak: "break-all" }}>
                        {e.bind_url}
                      </Typography.Text>
                    ) : (
                      <Typography.Text type="secondary">—</Typography.Text>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 6 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>📲 Ключ VPN:</Typography.Text>
                  <div>
                    <Typography.Text code copyable style={{ fontSize: 12, wordBreak: "break-all" }}>
                      {e.sub_url}
                    </Typography.Text>
                  </div>
                </div>
              </div>
              {i < exportResults.length - 1 && <Divider style={{ margin: 0 }} />}
            </div>
          ))}
        </div>
      </Modal>
    </List>
  );
};
